import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useMapStore } from '../../store/useMapStore';
import { useTranslation } from '../../i18n/translations';
import { getLayerName, getLayerDescription, getLayerSlug, getLayerFromSlug } from '../../utils/routeUtils';
import { SITE_DESCRIPTION, SITE_NAME, buildSiteUrl } from '../../constants/site';
import { ENABLE_LAND_PARCEL } from '../../config/features';
import { useStripDefaultMeta } from '../../hooks/useStripDefaultMeta';
import { resolveEntitySeo } from '../../seo';
import type { ServiceLayer } from '../../types/gis';

const SEO: React.FC = () => {
  const { language } = useTranslation();
  const location = useLocation();
  const activeLayer = useMapStore(state => state.activeLayer);
  const activeDistrict = useMapStore(state => state.activeDistrict);
  const searchResult = useMapStore(state => state.searchResult);
  const selectedPdsShop = useMapStore(state => state.selectedPdsShop);
  const selectedPoliceStation = useMapStore(state => state.selectedPoliceStation);
  const selectedHealthFacility = useMapStore(state => state.selectedHealthFacility);
  const selectedRegistrationOffice = useMapStore(state => state.selectedRegistrationOffice);
  const selectedLocalBodyV2 = useMapStore(state => state.selectedLocalBodyV2);
  const selectedVeterinaryFacility = useMapStore(state => state.selectedVeterinaryFacility);
  const selectedLandParcel = useMapStore(state => state.selectedLandParcel);
  const jurisdictionDetails = useMapStore(state => state.jurisdictionDetails);
  const selectedTemple = useMapStore(state => state.selectedTemple);
  const selectedRadioStation = useMapStore(state => state.selectedRadioStation);
  const selectedFuelStation = useMapStore(state => state.selectedFuelStation);

  // SEO renders a full og/twitter set, so drop the static index.html defaults to
  // avoid duplicate, generic social tags in the live DOM.
  useStripDefaultMeta();

  const pathParts = location.pathname.split('/').filter(Boolean);
  const routeLayerSlug = pathParts[0] || null;
  const routeSegment = pathParts[1] ? decodeURIComponent(pathParts[1]) : null;
  const resolvedRouteLayer = routeLayerSlug ? getLayerFromSlug(routeLayerSlug) : null;
  const hasInvalidLayerPath = !!routeLayerSlug && !resolvedRouteLayer;

  const routeLayer = resolvedRouteLayer || activeLayer;
  const layerForSeo: ServiceLayer = routeLayer;
  const layerName = getLayerName(layerForSeo);
  const layerDesc = getLayerDescription(layerForSeo);
  const routeSlug = getLayerSlug(layerForSeo);

  const routePin = routeSegment && /^\d{6}$/.test(routeSegment) ? routeSegment : null;
  const routeDistrict = routeSegment && !routePin ? routeSegment : null;

  const currentPinFromResult = (
    searchResult?.properties?.pin_code ||
    searchResult?.properties?.PIN_CODE ||
    searchResult?.properties?.pincode
  )?.toString() || null;

  const pinForSeo = routePin || currentPinFromResult;
  const districtForSeo = routeDistrict || activeDistrict;

  const getPageTitle = () => {
    if (pinForSeo) return `${layerName} for Pincode ${pinForSeo} | ${SITE_NAME}`;
    if (districtForSeo) return `${layerName} in ${districtForSeo} District | ${SITE_NAME}`;

    if (constituencyFeature) return entitySeo!.title;

    if (searchResult) {
      const locationName = searchResult.properties.office_name ||
        searchResult.properties.district ||
        searchResult.properties.NAME ||
        pinForSeo ||
        'Tamil Nadu';
      return `${layerName} in ${locationName} | ${SITE_NAME}`;
    }

    if (selectedPdsShop) return entitySeo!.title;
    if (selectedPoliceStation) return entitySeo!.title;
    if (selectedHealthFacility) return entitySeo!.title;
    if (selectedRegistrationOffice) return entitySeo!.title;
    if (selectedLocalBodyV2) return entitySeo!.title;
    if (selectedVeterinaryFacility) return entitySeo!.title;
    if (ENABLE_LAND_PARCEL && selectedLandParcel) return entitySeo!.title;
    if (jurisdictionDetails) return entitySeo!.title;
    if (selectedTemple) return entitySeo!.title;
    if (selectedRadioStation) return entitySeo!.title;
    if (selectedFuelStation) return entitySeo!.title;

    if (layerForSeo === 'PDS') {
      return `Ration Shop Finder — Find Address & Phone by Shop Number | Tamil Nadu PDS | ${SITE_NAME}`;
    }

    return `${SITE_NAME} | Tamil Nadu ${layerName} Locator`;
  };

  const getPageUrl = (lang?: 'en' | 'ta') => {
    if (hasInvalidLayerPath) {
      return buildSiteUrl('/constituency');
    }
    const detailPart = routeSegment ? `/${encodeURIComponent(routeSegment)}` : '';
    const base = buildSiteUrl(`/${routeSlug}${detailPart}`);
    const targetLang = lang || language;
    if (targetLang === 'ta') {
      return `${base}?lang=ta`;
    }
    return base;
  };

  const pageUrl = getPageUrl();
  const enUrl = getPageUrl('en');
  const taUrl = getPageUrl('ta');

  // A constituency has no dedicated store selection — it rides on searchResult.
  // Treat it as a resolvable entity only when the constituency layer is active
  // and the result carries an AC/PC identity.
  const constituencyFeature = (layerForSeo === 'CONSTITUENCY' && searchResult &&
    (searchResult.properties.assembly_c || searchResult.properties.parliame_1))
    ? searchResult
    : null;

  // Entity-level SEO (title/description/breadcrumb/schema) for modules migrated
  // onto the resolver framework. Returns null for layers still handled inline.
  const entitySeo = resolveEntitySeo({
    language,
    siteName: SITE_NAME,
    layerName,
    pageUrl,
    selections: {
      pdsShop: selectedPdsShop,
      tnebSection: jurisdictionDetails,
      policeStation: selectedPoliceStation,
      healthFacility: selectedHealthFacility,
      registrationOffice: selectedRegistrationOffice,
      localBody: selectedLocalBodyV2,
      veterinaryFacility: selectedVeterinaryFacility,
      landParcel: ENABLE_LAND_PARCEL ? selectedLandParcel : null,
      temple: selectedTemple,
      constituency: constituencyFeature,
      radioStation: selectedRadioStation,
      fuelStation: selectedFuelStation
    }
  });

  const getPageDescription = () => {
    if (pinForSeo) {
      return `Explore ${layerDesc} for Tamil Nadu pincode ${pinForSeo}. View map boundaries, nearby services, and jurisdiction details on ${SITE_NAME}.`;
    }
    if (districtForSeo) {
      return `Explore ${layerDesc} across ${districtForSeo} District, Tamil Nadu with precise GIS boundaries, search, and service-level map details.`;
    }
    if (constituencyFeature) return entitySeo!.description;
    if (searchResult) {
      const locationName = searchResult.properties.office_name ||
        searchResult.properties.district ||
        searchResult.properties.NAME ||
        'Tamil Nadu';
      return `Find ${layerName} details for ${locationName} with precision GIS mapping and instant search on ${SITE_NAME}.`;
    }
    if (selectedPdsShop) return entitySeo!.description;
    if (selectedPoliceStation) return entitySeo!.description;
    if (selectedHealthFacility) return entitySeo!.description;
    if (selectedRegistrationOffice) return entitySeo!.description;
    if (selectedLocalBodyV2) return entitySeo!.description;
    if (selectedVeterinaryFacility) return entitySeo!.description;
    if (ENABLE_LAND_PARCEL && selectedLandParcel) return entitySeo!.description;
    if (jurisdictionDetails) return entitySeo!.description;
    if (selectedTemple) return entitySeo!.description;
    if (selectedRadioStation) return entitySeo!.description;
    if (selectedFuelStation) return entitySeo!.description;

    if (layerForSeo === 'PDS') {
      return `Find any Tamil Nadu ration shop (PDS / fair price shop) by its shop code or number. Get the shop name, address, in-charge name, phone number, and exact map location instantly — a free ration shop finder covering 34,000+ fair price shops across all districts on ${SITE_NAME}.`;
    }

    return `${SITE_DESCRIPTION} Explore Tamil Nadu with precision GIS mapping and global search.`;
  };

  const pageTitle = getPageTitle();
  const pageDescription = getPageDescription();
  const locale = language === 'ta' ? 'ta_IN' : 'en_IN';
  const alternateLocale = language === 'ta' ? 'en_IN' : 'ta_IN';
  const queryParams = new URLSearchParams(location.search);
  const hasShareParams = queryParams.has('f') || queryParams.has('sid');
  const robotsContent = (hasInvalidLayerPath || hasShareParams)
    ? 'noindex,nofollow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
    : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';
  const breadcrumbLabel = pinForSeo 
    ? `Pincode ${pinForSeo}` 
    : districtForSeo
    ? districtForSeo
    : constituencyFeature
    ? entitySeo!.breadcrumbLabel
    : selectedPdsShop
    ? entitySeo!.breadcrumbLabel
    : selectedPoliceStation
    ? entitySeo!.breadcrumbLabel
    : selectedHealthFacility
    ? entitySeo!.breadcrumbLabel
    : selectedRegistrationOffice
    ? entitySeo!.breadcrumbLabel
    : selectedLocalBodyV2
    ? entitySeo!.breadcrumbLabel
    : selectedVeterinaryFacility
    ? entitySeo!.breadcrumbLabel
    : (ENABLE_LAND_PARCEL && selectedLandParcel)
    ? entitySeo!.breadcrumbLabel
    : jurisdictionDetails
    ? entitySeo!.breadcrumbLabel
    : selectedTemple
    ? entitySeo!.breadcrumbLabel
    : selectedRadioStation
    ? entitySeo!.breadcrumbLabel
    : selectedFuelStation
    ? entitySeo!.breadcrumbLabel
    : null;
  const deepLinkSchema = breadcrumbLabel
    ? {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: pageTitle,
      description: pageDescription,
      url: pageUrl,
      isPartOf: buildSiteUrl('/'),
      inLanguage: language === 'ta' ? 'ta-IN' : 'en-IN',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: SITE_NAME,
            item: buildSiteUrl('/')
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: layerName,
            item: buildSiteUrl(`/${routeSlug}`)
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: breadcrumbLabel,
            item: pageUrl
          }
        ]
      }
    }
    : null;

  // Rich structured data for the migrated modules (PDS / TNEB) comes from the
  // resolver; normalise to an array so the render can map uniformly.
  const entitySchema = entitySeo?.schema
    ? (Array.isArray(entitySeo.schema) ? entitySeo.schema : [entitySeo.schema])
    : [];

  return (
    <Helmet>
      <html lang={language === 'ta' ? 'ta' : 'en'} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={pageUrl} />
      <link rel="alternate" hrefLang="en-IN" href={enUrl} />
      <link rel="alternate" hrefLang="ta-IN" href={taUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={locale} />
      <meta property="og:locale:alternate" content={alternateLocale} />
      
      {/* OpenGraph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={buildSiteUrl('/branding/og-image.webp')} />
      <meta property="og:image:alt" content={`${SITE_NAME} Tamil Nadu civic GIS map`} />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:site" content="@NammaMap" />
      <meta property="twitter:url" content={pageUrl} />
      <meta property="twitter:title" content={pageTitle} />
      <meta property="twitter:description" content={pageDescription} />
      <meta property="twitter:image" content={buildSiteUrl('/branding/og-image.webp')} />

      {deepLinkSchema && (
        <script type="application/ld+json">
          {JSON.stringify(deepLinkSchema)}
        </script>
      )}

      {entitySchema.map((schema, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
